import { Card, Avatar, Badge, Box, Button, InlineGrid, InlineStack, Icon, Link, OptionList, Text, TextField, BlockStack, Tabs, Divider } from "@shopify/polaris"
import { SendIcon } from "@shopify/polaris-icons";
import { useEffect, useState } from "react";
// import EmailRichTextEditor from "../EmailRichTextEditor";

import AppLayout from "../components/layout";
import { usePartnerUp } from "../providers/PartnerUpStoreProvider"

import { getBestAvatar, timeAgo } from "../utils";

import ReplySvg from '../assets/reply.svg'
import { getFunctions, httpsCallable } from "firebase/functions";

const sendEmail = httpsCallable(getFunctions(), 'sendEmail');

export default function EmailsWindow() {
  const { allEmails, leads, initialCreatorEmail, store, setToast, user } = usePartnerUp();

  const initialCreatorID = new URLSearchParams(window.location.search).get('creatorID')
  const [selectedCreator, setSelectedCreator] = useState(initialCreatorID || allEmails?.[0]?.creatorID)

  const [newEmail, setNewEmail] = useState('')
  const [messagesTab, setMessagesTab] = useState(0)
  const [messageSending, setMessageSending] = useState(false)

  const emailsByCreator = allEmails?.reduce((acc, email) => {
    if (!email?.creatorID) return acc
    if (!acc[email.creatorID]) {
      acc[email.creatorID] = []
    }
    acc[email.creatorID].push(email)
    return acc
  }, {}) || []

  const selectedCreatorFields = leads.find(lead => lead.id === selectedCreator)
  const selectedCreatorEmails = emailsByCreator[selectedCreator]

  useEffect(() => {
    markAsRead()
  }, [selectedCreator])

  async function sendNewMessage() {
    const subject = selectedCreatorEmails?.[0]?.subject || "It's " + user.name + ' From ' + store?.name
    try {
      setMessageSending(true);
      await sendEmail({
        creatorID: selectedCreator,
        subject,
        body: newEmail,
      })
      setNewEmail('');
      setToast({
        content: 'Email sent!',
      })
    } catch (error) {
      setToast({
        content: 'Failed to Send Email',
        error: true,
      })
      console.error(error);
    } finally {
      setMessageSending(false);
    }
  }

  async function markAsRead() {
    /*
    try {
      const unreadCreatorMessages = selectedCreatorEmails?.filter(email => email?.authorType !== 'store' && !email?.read)
      console.log('marking as read', unreadCreatorMessages)
      if (!unreadCreatorMessages?.length) return
      await authFetch("/api/messages/read", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageIDs: unreadCreatorMessages?.map(email => email.id)
        })
      });
    } catch (error) {
      console.error(error);
    }
    */
  }

  if (!Object?.values(emailsByCreator)?.length && !initialCreatorEmail) {
    return (
      <AppLayout>
        <Box paddingBlockEnd="32">
          <Card>
            <BlockStack gap="200" padding="5">
              <Text variant="headingLg">No Emails Yet</Text>
              <Text>Visit a creator's profile to start an email thread</Text>
            </BlockStack>
          </Card>
        </Box>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <BlockStack gap="300">
        <BlockStack gap="100">
          <InlineStack gap="200" align="start" blockAlign="center">
            <InlineStack>
              <Icon source={SendIcon} />
            </InlineStack>
            <Text variant="headingLg">Messages</Text>
          </InlineStack>
          <Text tone="subdued">Emails will both appear here and in your inbox. You can reply either way.</Text>
        </BlockStack>
        <Box paddingBlockEnd="32">
          <Card padding="0">
            <InlineGrid columns="1fr 3fr">
              <div style={{ borderRight: '1px solid #f1f1f1', height: '80%', overflowY: 'scroll' }}>
                <Box padding="0">
                  <Tabs fitted tabs={[
                    {
                      id: 'all',
                      content: 'All',
                      accessibilityLabel: 'All',
                    },
                    {
                      id: 'replied',
                      content: 'Replied',
                      accessibilityLabel: 'Replied',
                    },
                    {
                      id: 'unread',
                      content: 'Unread',
                      accessibilityLabel: 'Unread',
                    },
                  ]} selected={messagesTab} onSelect={setMessagesTab} />
                  <Divider />
                  <OptionList
                    selected={[selectedCreator]}
                    onChange={v => setSelectedCreator(v[0])}
                    options={Object.values(emailsByCreator || {})?.filter(cEmails => {
                      if (messagesTab === 0) return true
                      if (messagesTab === 1) return cEmails?.some(email => email?.authorType !== 'store')
                      if (messagesTab === 2) return cEmails?.some(email => email?.authorType !== 'store' && email?.read === false)
                    })?.map(creatorEmails => {
                      const creator = leads.find(lead => lead.id === creatorEmails[0]?.creatorID)
                      const lastMessage = creatorEmails[0]
                      const fromStore = lastMessage?.authorType === 'store'
                      const isUnread = lastMessage?.authorType !== 'store' && (lastMessage?.read === false)
                      return (
                        {
                          label: (
                            <div style={{ width: '100%' }}>
                              <InlineStack gap="200" align="start" blockAlign="center">
                                <Avatar size="xl" shape="square" initials={creator.name?.[0]} source={getBestAvatar(creator)} />
                                <BlockStack>
                                  <InlineStack align="start" gap="1">
                                    <Text variant="headingSm">
                                      {creator.name}
                                    </Text>
                                    <Text variant="bodySm">
                                      {
                                        fromStore ? <img style={{ width: '15px', height: '15px' }} src={ReplySvg} /> : isUnread ? <Badge status="info">1</Badge> : null
                                      }
                                    </Text>
                                  </InlineStack>
                                  <Text>{timeAgo(new Date(lastMessage?.timestamp))}</Text>
                                </BlockStack>
                              </InlineStack>
                            </div>
                          ),
                          value: creatorEmails[0]?.creatorID,
                        }
                      )
                    })} />
                </Box>
              </div>
              <BlockStack>
                <div style={{ background: 'white', borderBottom: '1px solid #f1f1f1', borderLeft: '1px solid #f1f1f1', padding: '10px' }}>
                  <InlineStack gap="200" align="start" blockAlign="center">
                    <Avatar size="xl" shape="square" initials={selectedCreatorFields?.name?.[0]} source={getBestAvatar(selectedCreatorFields)} />
                    <BlockStack>
                      <Text variant="headingSm">
                        <Link removeUnderline onClick={() => handleCreatorClick(selectedCreatorFields.id)}>
                          {selectedCreatorFields?.name}
                        </Link>
                      </Text>
                      <Text>{selectedCreatorFields?.email || 'No Email'}</Text>
                    </BlockStack>
                  </InlineStack>
                </div>
                <div style={{ height: '65vh', overflowY: 'scroll', display: 'flex', flexDirection: 'column-reverse', backgroundColor: '#F1F2F4' }}>
                  {selectedCreatorEmails?.map((email, index) => {
                    if (email?.authorType === 'store') {
                      return (
                        <div style={{ display: 'flex', justifyContent: 'end', margin: '10px' }}>
                          <div style={{ maxWidth: '50%', background: 'white' }}>
                            <Card>
                              <BlockStack gap="300">
                                <InlineStack key={index} gap="300" align="start">
                                  <div dangerouslySetInnerHTML={{ __html: email.body }}></div>
                                </InlineStack>
                                <InlineStack align="end">
                                  <Text variant="bodySm" color="subdued">
                                    {timeAgo(new Date(email?.timestamp))}
                                  </Text>
                                </InlineStack>
                              </BlockStack>
                            </Card>
                          </div>
                        </div>
                      )
                    } else {
                      return (
                        <div style={{ display: 'flex', justifyContent: 'start', margin: '10px 0', marginLeft: '20%' }}>
                          <div style={{ maxWidth: '50%' }}>
                            <Card background={'bg-success-subdued'}>
                              <InlineStack key={index} gap="300" align="start">
                                <div dangerouslySetInnerHTML={{ __html: email.body }}></div>
                              </InlineStack>
                              <InlineStack align="end">
                                <Text variant="bodySm" color="subdued">
                                  {timeAgo(new Date(email?.timestamp))}
                                </Text>
                              </InlineStack>
                            </Card>
                          </div>
                        </div>
                      )
                    }
                  })}
                </div>
                <div style={{ background: 'white', padding: '5px' }}>
                  <InlineGrid columns="8fr 1fr" alignItems="end" gap="300">
                    <TextField value={newEmail} onChange={setNewEmail} placeholder="Type an email..." />
                    <Button loading={messageSending} disabled={!newEmail || !selectedCreatorFields?.email} icon={SendIcon} onClick={sendNewMessage}>Send</Button>
                  </InlineGrid>
                </div>
              </BlockStack>
            </InlineGrid>
          </Card>
        </Box>
      </BlockStack>
    </AppLayout>
  )
}

